import React from 'react';
import { FaPrint, FaArrowLeft } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { BASE_URL } from '../../../../utils/api';
import './PaymentVoucher.css';

const API_BASE = BASE_URL;

const MONTH_NAMES = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];

const SlipContent = ({ s, emp, col, colName, colAddress, colHeader, logoPath, slipNo, salaryMonth, paidDate, copyLabel }) => {
  const base      = s.monthlySalary     || 0;
  const pfAmt     = s.pfDeduction       || 0;
  const esiAmt    = s.esiDeduction      || 0;
  const otherDed  = s.otherDeduction    || 0;
  const advAdj    = s.advanceAdjust     || 0;
  const secDed    = s.securityDeduction || 0;
  const secRet    = s.securityReturn    || 0;
  const earned    = s.earnedSalary      || 0;
  const netPay    = s.netSalary         || 0;
  const dailyRate = base / 30;

  return (
    <div className="pv-slip">

      {/* Copy label */}
      <div className="pv-copy-label">{copyLabel}</div>

      {/* Header: logo left, college info center */}
      <div className="pv-header">
        {logoPath && (
          <div className="pv-logo-box">
            <img src={logoPath} alt="Logo" className="pv-logo-img"
              onError={e => { e.target.onerror = null; e.target.parentNode.style.display = 'none'; }} />
          </div>
        )}
        <div className={`pv-header-info${logoPath ? '' : ' pv-header-info-full'}`}>
          <h1 className="pv-college-name">{colName}</h1>
          {colHeader  && <p className="pv-college-title">{colHeader}</p>}
          {colAddress && <p className="pv-college-sub">{colAddress}</p>}
        </div>
      </div>

      {/* Slip Title */}
      <div className="pv-slip-title-bar">SALARY SLIP</div>

      {/* Meta — slip number + month + slip date */}
      <div className="pv-meta">
        <div className="pv-meta-item"><span className="pv-meta-label">Slip No:</span><span className="pv-meta-value pv-bold">{slipNo}</span></div>
        <div className="pv-meta-item"><span className="pv-meta-label">Month:</span><span className="pv-meta-value pv-bold">{salaryMonth}</span></div>
        <div className="pv-meta-item"><span className="pv-meta-label">Slip Date:</span><span className="pv-meta-value">{paidDate}</span></div>
      </div>

      {/* Employee + Salary Structure */}
      <div className="pv-grid-2">
        <div className="pv-box">
          <div className="pv-box-title">Employee Info</div>
          <div className="pv-row"><span className="pv-label">Name</span><span className="pv-value">{emp.name || '—'}</span></div>
          <div className="pv-row"><span className="pv-label">Department</span><span className="pv-value">{emp.department?.name || '—'}</span></div>
          <div className="pv-row"><span className="pv-label">Payment Mode</span><span className="pv-value pv-bold">{s.paymentMode || '—'}</span></div>
          <div className="pv-row"><span className="pv-label">Payment Date</span><span className="pv-value">{paidDate}</span></div>
          {s.paymentMode !== 'Cash' && s.paymentRef && (
            <div className="pv-row"><span className="pv-label">Reference Number</span><span className="pv-value pv-bold">{s.paymentRef}</span></div>
          )}
        </div>
        <div className="pv-box">
          <div className="pv-box-title">Salary Structure</div>
          <div className="pv-row"><span className="pv-label">Monthly Salary</span><span className="pv-value">₹ {base.toFixed(2)}</span></div>
          <div className="pv-row"><span className="pv-label">Daily Rate (÷30)</span><span className="pv-value">₹ {dailyRate.toFixed(2)}</span></div>
          <div className="pv-row"><span className="pv-label">PF Deduction</span><span className="pv-value pv-red">₹ {pfAmt.toFixed(2)}</span></div>
          <div className="pv-row"><span className="pv-label">ESI Deduction</span><span className="pv-value pv-red">₹ {esiAmt.toFixed(2)}</span></div>
        </div>
      </div>

      {/* Calculation */}
      <div className="pv-calc-section">
        <div className="pv-box-title">Salary Calculation</div>
        <div className="pv-calc-box">
          <div className="pv-calc-row"><span className="pv-calc-label">Earned Salary</span><span className="pv-calc-amt">₹ {earned.toFixed(2)}</span></div>
          {otherDed > 0 && <div className="pv-calc-row pv-deduct"><span className="pv-calc-label">Less: Other Deductions</span><span className="pv-calc-amt">- ₹ {otherDed.toFixed(2)}</span></div>}
          {advAdj   > 0 && <div className="pv-calc-row pv-deduct"><span className="pv-calc-label">Less: Advance Adjustment</span><span className="pv-calc-amt">- ₹ {advAdj.toFixed(2)}</span></div>}
          {secDed   > 0 && <div className="pv-calc-row pv-deduct"><span className="pv-calc-label">Less: Security Deduction</span><span className="pv-calc-amt">- ₹ {secDed.toFixed(2)}</span></div>}
          {secRet   > 0 && <div className="pv-calc-row pv-add"><span className="pv-calc-label">Add: Security Return</span><span className="pv-calc-amt">+ ₹ {secRet.toFixed(2)}</span></div>}
          <div className="pv-calc-total"><span className="pv-calc-label">Amount Paid</span><span className="pv-calc-amt">₹ {netPay.toFixed(2)}</span></div>
        </div>
      </div>

      {/* Footer: Authorised Signature right */}
      <div className="pv-footer">
        <div className="pv-signature">
          <div className="pv-sig-line" />
          <span>Authorised Signature</span>
        </div>
      </div>

    </div>
  );
};

const PaymentVoucher = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const basePath  = user?.role === 'collegeadmin' ? '/collegeadmin' : '/admin';

  const { salary, collegeName, from, empId } = location.state || {};

  const s   = salary || {};
  const emp = s.employee || {};
  const col = s.college  || {};

  const colName    = col.name        || collegeName || '—';
  const colAddress = col.address     || '';
  const colHeader  = col.titleHeader || '';
  const logoPath   = col.logo ? `${API_BASE}/${col.logo.replace(/\\/g, '/')}` : null;

  const slipNo      = s.slipNo || `SLP-${s.year}-${String(s._id || '').slice(-4).toUpperCase()}`;
  const salaryMonth = s.month  ? `${MONTH_NAMES[s.month - 1]} ${s.year}` : '—';
  const paidDate    = s.paidAt
    ? new Date(s.paidAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  const slipProps = { s, emp, col, colName, colAddress, colHeader, logoPath, slipNo, salaryMonth, paidDate };

  return (
    <div className="pv-container">

      {/* Action Buttons — screen only */}
      <div className="pv-actions">
        <button className="pv-back-btn" onClick={() =>
          from === 'log'
            ? navigate(`${basePath}/salary/log/${empId}`)
            : navigate(`${basePath}/salary`)
        }>
          <FaArrowLeft /> Back
        </button>
        <button className="pv-print-btn" onClick={() => window.print()}>
          <FaPrint /> Print Slip
        </button>
      </div>

      {/* A4 wrapper — contains 2 A5 slips */}
      <div className="pv-a4-wrapper">
        <SlipContent {...slipProps} copyLabel="Employee Copy" />
        <div className="pv-divider" />
        <SlipContent {...slipProps} copyLabel="Office Copy" />
      </div>

    </div>
  );
};

export default PaymentVoucher;
